/**
 * "Vista lagalista æfingarinnar fyrir ónettengt?" — a small prompt on the next-event card when the
 * event has a setlist that is not saved yet. "Ekki núna" hides it for that setlist on this device.
 */
import { CloudDownload } from 'lucide-react';
import { useState } from 'react';
import { useCollections } from '../api/library';
import { Button } from '../components/ui';
import { useT } from '../i18n';
import { canSaveOffline, useOfflineSnapshot, usePack } from './hooks';
import { SavePackSheet } from './SavePackSheet';

const DISMISS_KEY = 'kgr.offline.dismissedSetlists';

function readDismissed(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(DISMISS_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function dismiss(id: string): void {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify([...readDismissed().filter((x) => x !== id), id].slice(-20)));
  } catch {
    /* storage unavailable: hidden for this session only */
  }
}

export function SetlistOfflinePrompt({ setlistId }: { setlistId: string }) {
  const t = useT();
  const collections = useCollections();
  const pack = usePack('collection', setlistId);
  const { loaded } = useOfflineSnapshot();
  const [hidden, setHidden] = useState(() => readDismissed().includes(setlistId));
  const [open, setOpen] = useState(false);
  const coll = collections.data?.find((c) => c.id === setlistId);
  // The setlist must be visible to this user, and offline state must be known before prompting.
  if (!loaded || pack || hidden || !coll || coll.entries.length === 0 || !canSaveOffline()) return null;
  return (
    <div className="flex flex-col gap-3 rounded-2xl bg-[var(--accent-soft)] p-4 sm:flex-row sm:items-center">
      <p className="flex flex-1 items-start gap-2.5 font-semibold text-[var(--ink)]">
        <CloudDownload className="mt-0.5 size-5 shrink-0 text-[var(--accent)]" aria-hidden />
        {t('offline.next_up_prompt')}
      </p>
      <div className="flex gap-2">
        <Button
          variant="ghost"
          onClick={() => {
            dismiss(setlistId);
            setHidden(true);
          }}
        >
          {t('offline.next_up_dismiss')}
        </Button>
        <Button icon={CloudDownload} onClick={() => setOpen(true)}>
          {t('offline.save_short')}
        </Button>
      </div>
      <SavePackSheet open={open} onClose={() => setOpen(false)} kind="collection" refId={setlistId} title={coll.name} />
    </div>
  );
}
